import React, { useState, useEffect, useContext, useCallback } from 'react';
import { Table, Button, Modal, Form, Card, Row, Col, Alert, Container, Badge } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import api from '../../api/axios';
import AuthContext from '../../context/AuthContext';

const ManageShops = () => {
    const { user } = useContext(AuthContext);
    const [shops, setShops] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [modalError, setModalError] = useState('');
    const [isEditMode, setIsEditMode] = useState(false);
    const [selectedShopId, setSelectedShopId] = useState(null);
    
    const [shopForm, setShopForm] = useState({
        shopName: '',
        adminUsername: '',
        adminPassword: '',
        business_type: 'reservation',
        use_reservation: true,
        bay_count: 1
    });

    const fetchShops = useCallback(async () => {
        if (!user) return;
        try {
            const response = await api.get('/superadmin/shops');
            setShops(response.data.data);
        } catch (error) {
            console.error("Error fetching shops:", error);
        }
    }, [user]);

    useEffect(() => {
        if (user) {
            fetchShops();
        }
    }, [user, fetchShops]);

    const handleShowCreate = () => {
        setIsEditMode(false);
        setShopForm({
            shopName: '',
            adminUsername: '',
            adminPassword: '',
            business_type: 'reservation',
            use_reservation: true,
            bay_count: 1
        });
        setModalError('');
        setShowModal(true);
    };

    const handleShowEdit = (shop) => {
        setIsEditMode(true);
        setSelectedShopId(shop.id);
        setShopForm({
            shopName: shop.shopName,
            adminUsername: '', // 수정 모드에선 사용 안 함
            adminPassword: '',
            business_type: shop.business_type || 'reservation',
            use_reservation: shop.use_reservation === 1 || shop.use_reservation === true,
            bay_count: shop.bay_count || 1
        });
        setModalError('');
        setShowModal(true);
    };

    const handleCloseModal = () => setShowModal(false);

    const handleSave = async () => {
        if (!shopForm.shopName) {
            setModalError('매장 이름을 입력해주세요.');
            return;
        }

        try {
            if (isEditMode) {
                await api.put(`/superadmin/shops/${selectedShopId}`, {
                    shopName: shopForm.shopName,
                    use_reservation: shopForm.use_reservation,
                    bay_count: shopForm.use_reservation ? shopForm.bay_count : 1, // 비활성화 시 1로 초기화 혹은 유지
                    business_type: shopForm.business_type
                });
            } else {
                if (!shopForm.adminUsername || !shopForm.adminPassword) {
                    setModalError('관리자 계정 정보를 입력해주세요.');
                    return;
                }
                await api.post('/superadmin/shops', shopForm);
            }
            fetchShops();
            handleCloseModal();
        } catch (error) {
            const errorMessage = error.response?.data?.errors?.[0]?.msg || error.response?.data?.error || '알 수 없는 오류가 발생했습니다.';
            setModalError('저장에 실패했습니다: ' + errorMessage);
        }
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setShopForm(prev => {
            const newState = { 
                ...prev, 
                [name]: type === 'checkbox' ? checked : (type === 'number' ? parseInt(value, 10) : value)
            };
            return newState;
        });
    };

    return (
        <Container fluid className="py-4">
            <Row className="align-items-center mb-4">
                <Col>
                    <h3 className="mb-0 text-primary fw-bold">전체 매장 관리</h3>
                    <p className="text-muted small mb-0">신규 매장을 등록하거나 기존 매장의 설정을 변경합니다.</p>
                </Col>
                <Col xs="auto">
                    <Button variant="primary" onClick={handleShowCreate} className="px-4">
                        <i className="bi bi-plus-lg me-2"></i>새 매장 추가
                    </Button>
                </Col>
            </Row>

            <Card className="shadow-sm border-0">
                <Card.Body className="p-0">
                    <Table hover responsive className="mb-0 align-middle">
                        <thead className="table-light">
                            <tr>
                                <th className="ps-4">ID</th>
                                <th>매장명</th>
                                <th>운영 형태</th>
                                <th>예약 시스템</th>
                                <th>베이 수</th>
                                <th>상태</th>
                                <th className="text-end pe-4">관리</th>
                            </tr>
                        </thead>
                        <tbody>
                            {shops.map(shop => (
                                <tr key={shop.id}>
                                    <td className="ps-4 text-muted">{shop.id}</td>
                                    <td className="fw-bold">{shop.shopName}</td>
                                    <td>
                                        <Badge bg="light" text="dark" className="border">
                                            {shop.business_type === 'reservation' ? '예약 중심' : '현장 중심'}
                                        </Badge>
                                    </td>
                                    <td>
                                        {shop.use_reservation ? 
                                            <Badge bg="success-subtle" text="success">활성화</Badge> : 
                                            <Badge bg="secondary-subtle" text="secondary">비활성화</Badge>
                                        }
                                    </td>
                                    <td>{shop.use_reservation ? `${shop.bay_count}개` : '-'}</td>
                                    <td>{shop.isActive ? '✅ 정상' : '❌ 정지'}</td>
                                    <td className="text-end pe-4">
                                        <Button variant="outline-primary" size="sm" className="me-2" onClick={() => handleShowEdit(shop)}>
                                            수정
                                        </Button>
                                        <Button as={Link} to={`/superadmin/shops/${shop.id}/users`} variant="outline-secondary" size="sm">
                                            계정 관리
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </Card.Body>
            </Card>

            <Modal show={showModal} onHide={handleCloseModal} size="lg" centered>
                <Modal.Header closeButton className="bg-light">
                    <Modal.Title className="fw-bold">{isEditMode ? '매장 설정 수정' : '새 매장 및 관리자 생성'}</Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-4">
                    {modalError && <Alert variant="danger" className="py-2">{modalError}</Alert>}
                    <Form>
                        <Row className="mb-4">
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label className="fw-bold">매장 이름</Form.Label>
                                    <Form.Control type="text" name="shopName" value={shopForm.shopName} onChange={handleChange} placeholder="매장명을 입력하세요" />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label className="fw-bold">매장 운영 형태</Form.Label>
                                    <Form.Select name="business_type" value={shopForm.business_type} onChange={handleChange}>
                                        <option value="reservation">디테일링 / 손세차 (예약 중심)</option>
                                        <option value="onsite">주유소 / 자동세차 (현장 중심)</option>
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                        </Row>

                        <Row className="mb-4 p-3 rounded mx-0" style={{ background: 'var(--color-bg)' }}>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label className={`fw-bold ${!shopForm.use_reservation ? 'text-muted' : ''}`}>
                                        작업 공간(Bay) 개수
                                    </Form.Label>
                                    <Form.Control
                                        type="number"
                                        name="bay_count"
                                        value={shopForm.bay_count}
                                        onChange={handleChange}
                                        min="1"
                                        disabled={!shopForm.use_reservation}
                                    />
                                    <Form.Text className="text-muted small">예약 기능을 사용할 때만 설정 가능합니다.</Form.Text>
                                </Form.Group>
                            </Col>
                            <Col md={6} className="d-flex align-items-center">
                                <Form.Group className="w-100">
                                    <Form.Check 
                                        type="switch"
                                        id="use-reservation-switch"
                                        label="예약 시스템 활성화"
                                        name="use_reservation"
                                        checked={shopForm.use_reservation}
                                        onChange={handleChange}
                                        className="fw-bold"
                                    />
                                </Form.Group>
                            </Col>
                        </Row>

                        {!isEditMode && (
                            <>
                                <hr className="my-4" />
                                <h6 className="mb-3 fw-bold text-primary">매장 관리자 계정 설정</h6>
                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>아이디</Form.Label>
                                            <Form.Control type="text" name="adminUsername" value={shopForm.adminUsername} onChange={handleChange} autoComplete="off" />
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>비밀번호</Form.Label>
                                            <Form.Control type="password" name="adminPassword" value={shopForm.adminPassword} onChange={handleChange} autoComplete="new-password" />
                                            <Form.Text className="text-muted small">최소 6자 이상의 비밀번호를 설정하세요.</Form.Text>
                                        </Form.Group>
                                    </Col>
                                </Row>
                            </>
                        )}
                    </Form>
                </Modal.Body>
                <Modal.Footer className="bg-light">
                    <Button variant="secondary" onClick={handleCloseModal}>취소</Button>
                    <Button variant="primary" onClick={handleSave} className="px-4">
                        {isEditMode ? '수정 완료' : '매장 생성'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
};

export default ManageShops;
